import { useEffect, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { AxiosError } from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import styled from 'styled-components';
import environmentConfig from '../../config/environment-configs';
import PasswordStrengthBar from './PasswordStrengthBar';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../hooks/useAuth';
import Header from '../../components/Header';
import Container from '../../components/Container';
import Input from '../../components/Form/Input';
import Button from '../../components/Button';
import Title from '../../components/Title';
import Label from '../../components/Form/Label';
import Checkbox from '../../components/Form/Checkbox';
import { api } from '../../services/api';
import { FieldSet } from '../../components/Form/FieldSet/styles';
import { LoadingSpinner } from '../../components/Loading/styles';
import { Actions, Categories, Labels } from '../../types/gtm';
import { scrollToErrors } from '../../util/scrollToErrors';

const ChangePasswordBox = styled.div`
  display: flex;
  @media (max-width: 650px) {
    width: 95%;
  }
  > form {
    display: flex;
    flex-direction: column;
    > button {
      margin-top: 32px;
    }
    @media (max-width: 650px) {
      width: 100%;
      height: 100%;
    }
  }
`;

const LabelCheckbox = styled.label`
  display: flex;
  font-size: 14px;
  align-items: center;
  > input {
    margin-left: 0;
  }
`;

export const validationSchema = Yup.object().shape({
  currentPassword: Yup.string().required('Preenchimento obrigatório'),
  newPassword: Yup.string()
    .required('Preenchimento obrigatório')
    .min(8, 'Sua senha precisa ter de 8 a 15 caracteres')
    .max(15, 'Sua senha precisa ter de 8 a 15 caracteres'),
  newPasswordConfirmation: Yup.string()
    .required('Preenchimento obrigatório')
    .oneOf([Yup.ref('newPassword')], 'As senhas não coincidem'),
});

interface ChangePasswordBodyRequest {
  currentPassword: string;
  newPassword: string;
  newPasswordConfirmation: string;
  logoutSessions: boolean;
}

type ResetFormType = FormikHelpers<ChangePasswordBodyRequest>['resetForm'];

const ChangePasswordPage = () => {
  const { user, logout, authenticateOnAPI } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.dataLayer.push({
      event: 'customEventUserInteraction',
      event_category: Categories.EditUserPassword,
      event_action: Actions.Pageview,
      bd_suser_code: user?.globoId,
    });
  }, []);

  const getRecaptchaToken = async (): Promise<string> => {
    return new Promise(resolve => {
      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(environmentConfig.GOOGLE_RECAPTCHA_KEY, {
            action: 'passwordMinhaConta',
          })
          .then((token: string) => {
            resolve(token);
          });
      });
    });
  };

  const changePasswordRequest = async (
    values: ChangePasswordBodyRequest,
    resetForm: ResetFormType,
  ) => {
    try {
      const body = {
        ...values,
      };

      setIsSubmitting(true);

      const token = await authenticateOnAPI();
      const recaptchaToken = await getRecaptchaToken();

      const response = await api.put('/user/password', body, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Recaptcha-Token': recaptchaToken,
        },
      });

      window.dataLayer.push({
        event: 'customEventUserInteraction',
        event_category: Categories.EditUserPassword,
        event_action: Actions.Success,
        event_label: Labels.SuccessChangedPassword,
        bd_suser_code: user?.globoId,
      });

      window.dataLayer.push({
        event: 'customEventUserInteraction',
        event_category: Categories.EditUserPassword,
        event_action: Actions.SignOutAllSessions,
        event_label: body.logoutSessions ? 'Ligado' : 'Desligado',
        bd_suser_code: user?.globoId,
      });

      if (response.status === 207) {
        toast.warning(
          'A senha foi alterada com sucesso, porém não foi possível desconectar sua conta de todos os dispositivos.',
          { autoClose: 10000 },
        );
      } else {
        toast.success('Senha alterada com sucesso');
      }
      resetForm();
    } catch (error) {
      const errorRequest = error as AxiosError;
      let labelError: string | undefined = Labels.PasswordNotChanged;

      switch (errorRequest.response?.status) {
        case 412:
          labelError = Labels.CurrentPasswordInvalid;
          toast.error('Senha atual inválida');
          break;
        case 409:
          labelError = Labels.NewPasswordSameCurrentPassword;
          toast.error('Essa senha já foi utilizada');
          break;
        case 411:
          toast.error('Sua senha precisa ter de 8 a 15 caracteres');
          break;
        case 417:
          toast.error('As senhas não coincidem');
          break;
        default:
          toast.error('Ocorreu um erro inesperado. Tente novamente.');
          break;
      }

      window.dataLayer.push({
        event: 'customEventUserInteraction',
        event_category: Categories.EditUserPassword,
        event_action: Actions.Error,
        event_label: labelError,
        bd_suser_code: user?.globoId,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        theme="colored"
        pauseOnHover={false}
      />

      <>
        <Header
          screenMenu={Categories.EditUserPassword}
          user={user}
          onLogout={logout}
          showReturn
          onReturn={({ handleSmartReturn }) => {
            window.dataLayer.push({
              event: 'customEventUserInteraction',
              event_category: Categories.EditUserPassword,
              event_action: Actions.ClickBack,
              bd_suser_code: user?.globoId,
            });
            handleSmartReturn();
          }}
        />
        <Container>
          <ChangePasswordBox>
            <Formik
              validateOnChange
              validateOnMount
              initialValues={{
                currentPassword: '',
                newPassword: '',
                newPasswordConfirmation: '',
                logoutSessions: true,
              }}
              validationSchema={validationSchema}
              onSubmit={async (values, { resetForm }) => {
                await changePasswordRequest(values, resetForm);
              }}
            >
              {({
                values,
                handleSubmit,
                handleChange,
                errors,
                touched,
                handleBlur,
              }) => {
                return (
                  <form onSubmit={handleSubmit}>
                    <Title variant="page">Alterar senha</Title>

                    <FieldSet>
                      <Label htmlFor="currentPassword">Senha atual</Label>
                      <Input
                        type="password"
                        value={values.currentPassword}
                        placeHolderText="Informe sua senha atual"
                        name="currentPassword"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errorMessage={
                          errors.currentPassword && touched.currentPassword
                            ? errors.currentPassword
                            : ''
                        }
                      />
                    </FieldSet>

                    <FieldSet>
                      <Label htmlFor="newPassword">
                        Nova senha de 8 a 15 dígitos
                      </Label>
                      <Input
                        type="password"
                        value={values.newPassword}
                        placeHolderText="Informe sua nova senha"
                        name="newPassword"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errorMessage={
                          errors.newPassword && touched.newPassword
                            ? errors.newPassword
                            : ''
                        }
                      />
                      {values.newPassword.length >= 8 &&
                        values.newPassword.length <= 15 && (
                          <PasswordStrengthBar password={values.newPassword} />
                        )}
                    </FieldSet>

                    <FieldSet>
                      <Label htmlFor="newPasswordConfirmation">
                        Confirmação de nova senha
                      </Label>
                      <Input
                        type="password"
                        value={values.newPasswordConfirmation}
                        placeHolderText="Repita sua nova senha"
                        name="newPasswordConfirmation"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errorMessage={
                          errors.newPasswordConfirmation &&
                          touched.newPasswordConfirmation
                            ? errors.newPasswordConfirmation
                            : ''
                        }
                      />
                    </FieldSet>

                    <FieldSet>
                      <LabelCheckbox htmlFor="logoutSessions">
                        <Checkbox
                          id="logoutSessions"
                          name="logoutSessions"
                          onChange={handleChange}
                          checked={values.logoutSessions}
                        />
                        Sair da sua conta em todos os dispositivos
                      </LabelCheckbox>
                    </FieldSet>

                    <Button
                      onClick={() => {
                        window.dataLayer.push({
                          event: 'customEventUserInteraction',
                          event_category: Categories.EditUserPassword,
                          event_action: Actions.ClickSaveChanges,
                          bd_suser_code: user?.globoId,
                        });

                        scrollToErrors(errors);
                      }}
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <LoadingSpinner isButton />
                      ) : (
                        'Salvar alterações'
                      )}
                    </Button>
                  </form>
                );
              }}
            </Formik>
          </ChangePasswordBox>
        </Container>
      </>
    </>
  );
};

export default ChangePasswordPage;
